import {
  Component,
  OnInit,
  OnDestroy
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl } from '@angular/forms';
import { Observable, Subscription, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';

import { StandortService, Standort } from '../services/standort.service';
import { ObjektStrukturService, ObjektStrukturNode } from '../services/objekt-struktur.service';
import { KundeService, Kunde } from '../services/kunde.service';
import { MitarbeiterService, TechnicianOption } from '../services/mitarbeiter.service';
import { KontaktService, Kontakt } from '../services/kontakt.service';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { ProjektaufgabeComponent } from '../projektaufgabe/projektaufgabe.component';
import { LabelPreviewDialogComponent } from './label-preview-dialog/label-preview-dialog.component';
import { environment } from '../../environments/environment';

interface RoomIndicator {
  id: string;
  name: string;
  current_value: string;
  einheit: string;
  schwellenwert: string;
  mehr_weniger: number;
  indikator_number?: number;
  room?: {
    sektion?: {
      gebaeude?: {
        name: string;
      };
      name: string;
    };
  };
}

interface ReportContact {
  email: string;
  name: string;
  anrede: string;
}

@Component({
  selector: 'app-standort-edit',
  templateUrl: './standort_edit.component.html',
  styleUrls: ['./standort_edit.component.scss']
})
export class StandortEditComponent implements OnInit, OnDestroy {
  standortForm: UntypedFormGroup;
  standort: Standort | null = null;
  isLoading = false;
  isSaving = false;
  
  // Report contacts management
  reportContacts: ReportContact[] = [];
  newContactEmail: string = '';
  newContactName: string = '';
  newContactAnrede: string = '';
  roomIndicators?: RoomIndicator[];
  filteredRoomIndicators: RoomIndicator[] = [];
  isEditMode = false;
  isNewStandort = false;
  private subscription: Subscription;
  standortId: string | null = null;
  
  
  // Client management
  clients: Kunde[] = [];
  selectedClient: Kunde | null = null;
  kundeId: string | null = null;
  clientSearchControl = new FormControl();
  filteredClients: Kunde[] = [];

  // Techniker management
  availableTechnicians: TechnicianOption[] = [];
  isLoadingTechnicians = false;

  // Contact management
  availableContacts: Kontakt[] = [];
  isLoadingContacts = false;


  // Objekt-Struktur data
  objektStrukturData: ObjektStrukturNode[] = [];
  objektStrukturLoading = false;

  // Environment for template access
  environment = environment;

  // Status filter (Aktiv/Inaktiv) like client component
  statusFilterOptions = [
    { id: 1, label: 'Aktiv' },
    { id: 0, label: 'Inaktiv' },
    { id: -1, label: 'Alle' }
  ];
  selectedStatusFilter = 1; // default show only active
  isStatusMenuOpen = false;

  // Getter methods for template access
  get standorteStatusOptions() {
    return (environment as any).standorte_status || [];
  }

  get berichtSpracheOptions() {
    return (environment as any).bericht_sprache || [];
  }

  get berichtVersandOptions() {
    return (environment as any).bericht_versand || [];
  }

  /**
   * Get the effective date for "Datum ab wann"
   * If datum_ab_wann is set, use it; otherwise use the created date
   */
  getEffectiveDatumAbWann(): string {
    if (this.standort?.datum_ab_wann) {
      // Convert to German format (DD.MM.YYYY)
      return this.formatDateToGerman(this.standort.datum_ab_wann);
    }
    
    // If no datum_ab_wann is set, use created_at date
    if (this.standort?.created_at) {
      // Convert created_at to German format
      return this.formatDateToGerman(this.standort.created_at);
    }
    
    // Fallback to current date in German format
    return this.formatDateToGerman(new Date().toISOString());
  }

  /**
   * Format date to German format (DD.MM.YYYY)
   */
  private formatDateToGerman(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  }





  private destroy$ = new Subject<void>();

  constructor(
    private standortService: StandortService,
    private objektStrukturService: ObjektStrukturService,
    private kundeService: KundeService,
    private mitarbeiterService: MitarbeiterService,
    private kontaktService: KontaktService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    console.log('🏗️ StandortEditComponent constructor called');
    this.standortForm = this.createForm();
    console.log('📝 Form created successfully');
  }

  ngOnInit() {
    console.log('🏢 StandortEditComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    console.log('🔍 Route params:', this.route.snapshot.params);
    console.log('🔍 Route data:', this.route.snapshot.data);
    console.log('🔍 Route URL:', this.route.snapshot.url);
    
    // Check if this is the "new" route by looking at the URL segments
    const urlSegments = this.route.snapshot.url;
    const isNewRoute = urlSegments.length > 0 && urlSegments[urlSegments.length - 1].path === 'new';
    
    console.log('🔍 URL segments:', urlSegments);
    console.log('🔍 Is new route:', isNewRoute);
    
    if (isNewRoute) {
      console.log('🆕 This is the new standort route');
      this.standortId = null;
      this.isNewStandort = true;
      
      // Get kundeId from query parameters if coming from client-edit
      this.route.queryParams.subscribe(params => {
        this.kundeId = params['kundeId'];
        console.log('🔍 KundeId from query params:', this.kundeId);
        
        // Refresh flag is no longer needed since active projektaufgabe is shown in projektaufgabe-edit
        
        if (this.kundeId) {
          // Check if this is a kontakt UUID (coming from kontakt-edit) or client UUID (coming from client-edit)
          // If coming from kontakt-edit, we need to load the client through the kontakt relationship
          this.loadClientFromKontaktOrDirect(this.kundeId);
        } else {
          // Load all clients for dropdown selection
          this.loadClients();
        }
      });
      
      this.enterEditMode();
      
      // Set up autocomplete functionality
      this.setupClientAutocomplete();
    } else {
      // Get route parameters for existing standort
      this.route.params.subscribe(params => {
        console.log('📋 Route params received:', params);
        this.standortId = params['id'];
        this.isNewStandort = false;
        console.log('🆔 Standort ID:', this.standortId);
        console.log('🆕 Is new standort:', this.isNewStandort);
        
        if (this.standortId) {
          console.log('📋 Loading existing standort with ID:', this.standortId);
          this.loadStandort(this.standortId);
          // For existing standorte, start in view mode (not edit mode)
          this.isEditMode = false;
          
          // Set up autocomplete functionality for existing standorte too
          this.setupClientAutocomplete();
        }
      });
      
      // Refresh flag is no longer needed since active projektaufgabe is shown in projektaufgabe-edit
    }
    
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadClients() {
    console.log('📋 Loading all active clients for dropdown');
    this.kundeService.getActiveKundenForDropdown().subscribe({
      next: (response) => {
        this.clients = response.data || [];
        this.filteredClients = this.clients;
        console.log('✅ Loaded active clients:', this.clients.length);

        // Reconcile current form value with loaded clients
        const currentUuid: string = this.standortForm.get('kunde_id')?.value;
        if (currentUuid) {
          const matched = this.clients.find(c => c.uuid === currentUuid);
          if (matched) {
            this.selectedClient = matched;
            this.kundeId = matched.uuid;
            console.log('🔗 Reconciled client from form UUID:', matched.suchbegriff);
          } else {
            // Value is not a client UUID → clear and require selection
            this.selectedClient = null;
            this.kundeId = null;
            this.standortForm.patchValue({ kunde_id: '' });
            console.warn('⚠️ Form held a non-client UUID. Cleared. Please select a Kunde.');
          }
        }
      },
      error: (error) => {
        console.error('❌ Error loading clients:', error);
        this.snackBar.open('Fehler beim Laden der Kunden', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadClient(clientId: string) {
    console.log('📋 Loading specific client:', clientId);
    
    // Since we're now using UUIDs consistently, use getKundeByUuid
    this.kundeService.getKundeByUuid(clientId).subscribe({
      next: (response) => {
        this.selectedClient = response.data;
        this.kundeId = response.data.uuid; // Store the UUID
        console.log('✅ Loaded client via UUID:', this.selectedClient?.suchbegriff);
        
        // Load contacts for the client
        this.loadContacts();
      },
      error: (error) => {
        console.error('❌ Error loading client via UUID:', error);
        this.snackBar.open('Fehler beim Laden des Kunden', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadClientFromKontaktOrDirect(uuid: string) {
    console.log('🔍 Loading client from UUID (could be client or kontakt):', uuid);
    
    // First try to load as a client directly
    this.kundeService.getKundeByUuid(uuid).subscribe({
      next: (response) => {
        // Success - this was a client UUID
        this.selectedClient = response.data;
        this.kundeId = response.data.uuid;
        console.log('✅ Loaded client directly via UUID:', this.selectedClient?.suchbegriff);
        
        // Store the client UUID in the form
        this.standortForm.patchValue({ 
          kunde_id: response.data.uuid
        });
      },
      error: (error) => {
        console.log('❌ Not a client UUID, trying as kontakt UUID...');
        // If that fails, try to load as a kontakt and get the client from there
        this.loadClientFromKontakt(uuid);
      }
    });
  }

  setupClientAutocomplete() {
    // Initialize filtered clients with all clients
    this.filteredClients = this.clients;
    
    // Set up autocomplete for client selection
    this.clientSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (typeof value === 'object' && value !== null) {
          // A client object was selected
          console.log('🔄 Client selected from autocomplete:', value);
          console.log('🔄 Client UUID from autocomplete:', value.uuid);
          console.log('🔄 Client ID from autocomplete:', value.id);
          this.selectedClient = value;
          this.kundeId = value.uuid; // Use UUID, not integer ID
          // Store the client UUID directly in the form
          this.standortForm.patchValue({ 
            kunde_id: value.uuid // Use UUID, not integer ID
          });
          console.log('✅ Form updated from autocomplete with client UUID:', value.uuid);
          console.log('✅ Form value after autocomplete update:', this.standortForm.value);
          
          // Load contacts for the selected client
          this.loadContacts();
        } else if (typeof value === 'string') {
          // User is typing, filter clients
          this.filterClients(value);
        }
      });
  }


  filterClients(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.filteredClients = this.clients;
      return;
    }
    
    // Filter clients based on search term
    this.filteredClients = this.clients.filter(client => 
      client.suchbegriff.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.suchbegriff.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log('🔍 Filtered clients:', this.filteredClients.length);
  }

  onClientSelectionChange(event: any) {
    const client = event.value;
    console.log('🔄 Client selection changed:', client);
    console.log('🔄 Client UUID:', client?.uuid);
    console.log('🔄 Client ID:', client?.id);
    
    if (client && typeof client === 'object') {
      this.selectedClient = client;
      this.kundeId = client.uuid; // Use UUID, not integer ID
      // Store the client UUID directly in kunde_id
      this.standortForm.patchValue({ 
        kunde_id: client.uuid // Use UUID, not integer ID
      });
      console.log('✅ Form updated with client UUID:', client.uuid);
      console.log('✅ Form value after update:', this.standortForm.value);
    } else {
      this.selectedClient = null;
      this.kundeId = null;
      this.standortForm.patchValue({ 
        kunde_id: ''
      });
    }
  }

  // setPrimaryContactForKunde method removed - we now store client UUID directly

  displayClientName(client: Kunde): string {
    return client ? client.suchbegriff : '';
  }

  loadClientFromKontakt(kontaktId: string) {
    console.log('🔍 Loading client from kontakt ID:', kontaktId);
    
    // Get the kontakt first, then get the associated kunde
    this.kundeService.getKontakt(kontaktId).subscribe({
      next: (response) => {
        console.log('📋 Kontakt data received:', response.data);
        const kontakt = response.data;
        if (kontakt && kontakt.kunde_id) {
          console.log('📋 Found kontakt, loading associated kunde:', kontakt.kunde_id, 'Type:', typeof kontakt.kunde_id);
          
          // Load the client and wait for it to complete before setting form value
          this.kundeService.getKundeByUuid(kontakt.kunde_id).subscribe({
            next: (clientResponse) => {
              this.selectedClient = clientResponse.data;
              this.kundeId = clientResponse.data.uuid;
              console.log('✅ Loaded client from kontakt:', this.selectedClient?.suchbegriff);
              
              // Store the client UUID in the form after loading
              this.standortForm.patchValue({ 
                kunde_id: clientResponse.data.uuid
              });
              console.log('✅ Form updated with client UUID from kontakt:', clientResponse.data.uuid);
            },
            error: (clientError) => {
              console.error('❌ Error loading client from kontakt:', clientError);
              this.snackBar.open('Fehler beim Laden des Kunden', 'Schließen', { duration: 3000 });
            }
          });
        } else {
          console.warn('⚠️ Kontakt has no kunde_id:', kontakt);
        }
      },
      error: (error) => {
        console.error('❌ Error loading kontakt:', error);
        this.snackBar.open('Fehler beim Laden des Kontakts', 'Schließen', { duration: 3000 });
      }
    });
  }

  createForm() {
    this.standortForm = this.formBuilder.group({
      // nummer is not included in the form - it will be auto-generated by the database
      name: ['', [Validators.required]],
      status: [1],
      kunde_id: ['', [Validators.required]], // Direct client relationship
      techniker_id: [''], // Foreign key to mitarbeiter table (where is_techniker = true)
      bemerkung_struktur_anmerkung: [''],
      bericht_sprache: ['de'], // Default to Deutsch
      anmeldung_besuche: [''],
      bericht_versand: [''],
      besuchsbericht: [0],
      mit_auswertung_tabelle: [0],
      herkunft: [''],
      anrede: [''],
      bericht_email: [''], // JSON array of report contacts (no validation needed)
      // New date fields
      datum_ab_wann: [''],
      datum_bis: [''],
      // Address fields
      strasse: [''],
      plz: [''],
      ort: [''],
      land: ['']
    });
    
    console.log('📝 Form created with validators:');
    console.log('📝 name: required');
    console.log('📝 kunde_id: required');
    console.log('📝 Initial form valid:', this.standortForm.valid);
    console.log('📝 Initial form errors:', this.standortForm.errors);
    
    return this.standortForm;
  }

  loadStandort(id: string) {
    console.log('Loading location with ID:', id);
    this.isLoading = true;
    this.subscription = this.standortService.getStandort(id).subscribe(
      (response) => {
        console.log('Location data received:', response);
        this.standort = response.data;
        console.log('Setting form values with:', response.data);
        console.log('🔍 Full standort data structure:', JSON.stringify(response.data, null, 2));
        
        // Always populate form with standort data (needed for edit mode)
        const formData = {
          // nummer is not included in form data - it's auto-generated by database
          name: response.data.name || '',
          status: response.data.status || 1,
          kunde_id: response.data.kunde_id || '', // Direct client relationship
          techniker_id: response.data.techniker_id || '', // Foreign key to mitarbeiter table
          bemerkung_struktur_anmerkung: response.data.bemerkung_struktur_anmerkung || '',
          bericht_sprache: response.data.bericht_sprache || 'de',
          anmeldung_besuche: response.data.anmeldung_besuche || '',
          bericht_versand: response.data.bericht_versand || '',
          besuchsbericht: response.data.besuchsbericht || 0,
          mit_auswertung_tabelle: response.data.mit_auswertung_tabelle || 0,
          herkunft: response.data.herkunft || '',
          anrede: response.data.anrede || '',
          bericht_email: response.data.bericht_email || '',
          // New date fields
          datum_ab_wann: response.data.datum_ab_wann || (response.data.created_at ? new Date(response.data.created_at).toISOString().split('T')[0] : ''),
          datum_bis: response.data.datum_bis || '',
          // Address fields
          strasse: response.data.strasse || '',
          plz: response.data.plz || '',
          ort: response.data.ort || '',
          land: response.data.land || ''
        };
        
        console.log('Mapped form data:', formData);
        this.standortForm.patchValue(formData);
        console.log('Form values after patch:', this.standortForm.value);
        console.log('Form valid after patch:', this.standortForm.valid);
        console.log('Form errors after patch:', this.standortForm.errors);
        
        // Load client information directly from standort data
        console.log('🔍 Standort data received:', response.data);
        console.log('🔍 Has kunde data:', !!response.data.kunde);
        console.log('🔍 Has kunde_id:', !!response.data.kunde_id);
        console.log('🔍 kunde_id value:', response.data.kunde_id);
        
        if (response.data.kunde) {
          this.selectedClient = response.data.kunde;
          this.kundeId = response.data.kunde.uuid; // Use UUID, not integer ID
          console.log('✅ Client loaded directly from standort:', this.selectedClient?.suchbegriff);
          
          // Load contacts for the client
          this.loadContacts();
        } else if (response.data.kunde_id) {
          // Load client directly using kunde_id
          console.log('🔄 Loading client directly using kunde_id:', response.data.kunde_id);
          this.loadClient(response.data.kunde_id);
          // Ensure form holds the client UUID
          this.standortForm.patchValue({ kunde_id: response.data.kunde_id });
          // Also set the kundeId property for consistency
          this.kundeId = response.data.kunde_id;
          console.log('🔍 Set kundeId to:', this.kundeId);
          console.log('🔍 Form kunde_id value:', this.standortForm.get('kunde_id')?.value);
        } else {
          console.warn('⚠️ No client information found in standort data');
        }
        
        // Load all clients for dropdown (needed for edit mode)
        this.loadClients();
        
        // Load all technicians for dropdown (needed for edit mode)
        this.loadTechnicians();
        
        // Load objekt struktur
        this.loadObjektStruktur();
        
        // Load room indicators
        this.loadRoomIndicators();
        
        // Load report contacts
        this.loadReportContacts();
        
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading location:', error);
        this.isLoading = false;
        this.snackBar.open(`Error loading location: ${error.status} ${error.statusText}`, 'Close', {
          duration: 5000,
        });
      }
    );
  }

  saveStandort() {
    console.log('💾 === SAVE ATTEMPT DEBUG ===');
    console.log('💾 Form valid:', this.standortForm.valid);
    console.log('💾 Form invalid:', this.standortForm.invalid);
    console.log('💾 Form errors:', this.standortForm.errors);
    console.log('💾 Form value:', this.standortForm.value);
    
    // Debug each field before save
    Object.keys(this.standortForm.controls).forEach(key => {
      const control = this.standortForm.get(key);
      if (control && control.invalid) {
        console.log(`💾 INVALID FIELD "${key}":`, {
          errors: control.errors,
          value: control.value,
          touched: control.touched
        });
      }
    });
    
    if (this.standortForm.valid) {
      this.isSaving = true;
      const standortData = { ...this.standortForm.value } as any;

      // Normalize status to numeric ID
      if (typeof standortData.status === 'object' && standortData.status !== null) {
        standortData.status = standortData.status.id ?? standortData.status.value ?? standortData.status;
      }
      if (typeof standortData.status === 'string') {
        const parsed = parseInt(standortData.status, 10);
        if (!Number.isNaN(parsed)) standortData.status = parsed;
      }

      // Ensure kunde_id holds CLIENT UUID
      console.log('🔍 Save validation - selectedClient:', this.selectedClient);
      console.log('🔍 Save validation - selectedClient?.uuid:', this.selectedClient?.uuid);
      console.log('🔍 Save validation - standortData.kunde_id:', standortData.kunde_id);
      console.log('🔍 Save validation - kundeId:', this.kundeId);
      
      if (this.selectedClient?.uuid) {
        standortData.kunde_id = this.selectedClient.uuid;
        console.log('✅ Using selectedClient UUID:', this.selectedClient.uuid);
      } else if (standortData.kunde_id) {
        // If value exists, verify it matches a known client UUID; otherwise force user selection
        const matchesClient = this.clients.some(c => c.uuid === standortData.kunde_id);
        console.log('🔍 Checking if kunde_id matches known client:', matchesClient);
        console.log('🔍 Available client UUIDs:', this.clients.map(c => c.uuid));
        if (!matchesClient) {
          this.isSaving = false;
          this.snackBar.open('Bitte einen Kunden auswählen.', 'Schließen', { duration: 4000 });
          return;
        }
        console.log('✅ Using form kunde_id value:', standortData.kunde_id);
      } else {
        this.isSaving = false;
        this.snackBar.open('Bitte einen Kunden auswählen.', 'Schließen', { duration: 3000 });
        return;
      }
      
      // Handle empty date fields - convert empty strings to null
      if (standortData.datum_ab_wann === '') {
        standortData.datum_ab_wann = null;
      }
      if (standortData.datum_bis === '') {
        standortData.datum_bis = null;
      }
      
      console.log('💾 Saving standort with data:', standortData);
      console.log('💾 kunde_id value:', standortData.kunde_id);
      console.log('💾 techniker_id value:', standortData.techniker_id);
      console.log('💾 bericht_email value:', standortData.bericht_email);
      console.log('💾 datum_ab_wann value:', standortData.datum_ab_wann);
      console.log('💾 datum_bis value:', standortData.datum_bis);
      console.log('💾 Selected client:', this.selectedClient);
      console.log('💾 Selected client UUID:', this.selectedClient?.uuid);
      
      // nummer is not included - it will be auto-generated by the database

      const saveOperation = this.isNewStandort
        ? this.standortService.createStandort(standortData)
        : this.standortService.updateStandort(this.standort!.id, standortData);

      saveOperation.subscribe(
        (response) => {
          this.isSaving = false;
          
          this.snackBar.open(
            `Location ${this.isNewStandort ? 'created' : 'updated'} successfully`,
            'Close',
            { duration: 3000 }
          );
          
          if (this.isNewStandort) {
            // For new standorte, navigate back based on context
            const returnTo = this.route.snapshot.queryParams['returnTo'];
            const kundeId = this.route.snapshot.queryParams['kundeId'];
            
            if (returnTo === 'client-edit' && kundeId) {
              this.router.navigate(['/client-edit', kundeId]);
            } else {
              this.router.navigate(['/standorte']);
            }
          } else {
            // For existing standorte, stay in view mode
            this.isEditMode = false;
            this.standort = response.data; // Update local data
            console.log('✅ Switched to view mode after save');
          }
        },
        (error) => {
          this.isSaving = false;
          this.snackBar.open(
            `Error ${this.isNewStandort ? 'creating' : 'updating'} location`,
            'Close',
            { duration: 3000 }
          );
        }
      );
    }
  }

  cancel() {
    if (this.isNewStandort) {
      // For new standorte, navigate back based on context
      const returnTo = this.route.snapshot.queryParams['returnTo'];
      const kundeId = this.route.snapshot.queryParams['kundeId'];
      
      if (returnTo === 'client-edit' && kundeId) {
        // Return to client-edit with the specific kundeId
        this.router.navigate(['/client-edit', kundeId]);
      } else {
        // Default navigation back to standorte list
        this.router.navigate(['/standorte']);
      }
    } else {
      // For existing standorte, switch back to view mode
      this.isEditMode = false;
      console.log('✅ Switched to view mode after cancel');
    }
  }

  goBackToList() {
    // Check if we should return to a specific context (e.g., client-edit)
    const returnTo = this.route.snapshot.queryParams['returnTo'];
    const kundeId = this.route.snapshot.queryParams['kundeId'];
    
    if (returnTo === 'client-edit' && kundeId) {
      // Return to client-edit with the specific kundeId
      this.router.navigate(['/client-edit', kundeId]);
    } else {
      // Default navigation back to standorte list
      this.router.navigate(['/standorte']);
    }
  }

  createPlan() {
    console.log('📋 Creating new plan for standort:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }

    // Navigate to plan-edit component in create mode
    this.router.navigate(['/plan', 'new'], {
      queryParams: {
        standortId: this.standortId,
        mode: 'create',
        parentContext: 'standort-edit',
        returnTo: 'standort-edit',
        standortUuid: this.standortId
      }
    });
  }

  deleteStandort() {
    if (!this.standort) {
      this.snackBar.open('No location to delete', 'Close', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Standort löschen',
        message: `Sind Sie sicher, dass Sie den Standort "${this.standort.name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.standortService.deleteStandort(this.standort!.id).subscribe(
          (response) => {
            this.snackBar.open('Standort erfolgreich gelöscht', 'Close', {
              duration: 3000,
            });
            // Navigate back based on context
            const returnTo = this.route.snapshot.queryParams['returnTo'];
            const kundeId = this.route.snapshot.queryParams['kundeId'];
            
            if (returnTo === 'client-edit' && kundeId) {
              this.router.navigate(['/client-edit', kundeId]);
            } else {
              this.router.navigate(['/standorte']);
            }
          },
          (error) => {
            console.error('Error deleting location:', error);
            this.snackBar.open('Fehler beim Löschen des Standorts', 'Close', {
              duration: 5000,
            });
          }
        );
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.standortForm.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  getStatusText(statusId: number | any): string {
    if (typeof statusId === 'object' && statusId !== null) {
      // If status is an object, return its label or value
      return statusId.label || statusId.value || 'Unbekannt';
    }
    
    // If status is a number, find it in the environment array
    const statusArray = (environment as any).standorte_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }



  getBerichtSpracheText(spracheValue: string): string {
    const spracheArray = (environment as any).bericht_sprache || [];
    const sprache = spracheArray.find((s: any) => s.value === spracheValue);
    return sprache ? sprache.label : spracheValue;
  }

  enterEditMode() {
    this.isEditMode = true;
    
    // Load all active clients for dropdown in edit mode
    this.loadClients();
    
    // Patch the form with current location data
    if (this.standort) {
      this.standortForm.patchValue({
        // nummer is not included in form - it's auto-generated by database
        name: this.standort.name || '',
        status: this.standort.status || 1,
        kunde_id: this.standort.kunde_id || '',
        techniker_id: this.standort.techniker_id || '',
        bemerkung_struktur_anmerkung: this.standort.bemerkung_struktur_anmerkung || '',
        bericht_sprache: this.standort.bericht_sprache || 'de',
        anmeldung_besuche: this.standort.anmeldung_besuche || '',
        bericht_versand: this.standort.bericht_versand || '',
        besuchsbericht: this.standort.besuchsbericht || 0,
        mit_auswertung_tabelle: this.standort.mit_auswertung_tabelle || 0,
        herkunft: this.standort.herkunft || '',
        anrede: this.standort.anrede || '',
        bericht_email: this.standort.bericht_email || '',
        // New date fields
        datum_ab_wann: this.standort.datum_ab_wann || (this.standort.created_at ? new Date(this.standort.created_at).toISOString().split('T')[0] : ''),
        datum_bis: this.standort.datum_bis || '',
        // Address fields
        strasse: this.standort.strasse || '',
        plz: this.standort.plz || '',
        ort: this.standort.ort || '',
        land: this.standort.land || ''
      });
      
      console.log('🔧 Form patched in edit mode with kunde_id:', this.standort.kunde_id);
    }
  }



  /**
   * Load all available technicians for dropdown
   */
  loadTechnicians(): void {
    console.log('Loading technicians...');
    this.isLoadingTechnicians = true;
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        console.log('Technicians loaded:', response);
        this.availableTechnicians = response.data || [];
        this.isLoadingTechnicians = false;
        console.log('Available technicians count:', this.availableTechnicians.length);
      },
      error: (error) => {
        console.error('Error loading technicians:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoadingTechnicians = false;
      }
    });
  }

  /**
   * Load all available contacts for the selected client
   */
  loadContacts(): void {
    if (!this.kundeId) {
      console.log('No kunde ID available for loading contacts');
      this.availableContacts = [];
      return;
    }

    console.log('Loading contacts for kundeId:', this.kundeId);
    this.isLoadingContacts = true;
    this.kontaktService.getKontakteByKundeId(this.kundeId, 1, 100).subscribe({
      next: (response) => {
        console.log('Contacts loaded:', response);
        this.availableContacts = response.data || [];
        this.isLoadingContacts = false;
        console.log('Available contacts count:', this.availableContacts.length);
      },
      error: (error) => {
        console.error('Error loading contacts:', error);
        this.snackBar.open('Fehler beim Laden der Kontakte', 'Schließen', { duration: 3000 });
        this.isLoadingContacts = false;
        this.availableContacts = [];
      }
    });
  }

  /**
   * Get technician display name by ID
   */
  getTechnicianDisplayName(technikerId: string | number): string {
    if (!technikerId) {
      return '-';
    }
    const technician = this.availableTechnicians.find(t => t.id === Number(technikerId));
    return technician ? `${technician.display_name} (${technician.nummer}) - ${technician.funktion}` : `ID: ${technikerId}`;
  }

  /**
   * Handle contact selection from dropdown
   */
  onContactSelected(contact: Kontakt): void {
    if (contact && contact.email) {
      console.log('Contact selected:', contact.name, 'Email:', contact.email);
      this.standortForm.patchValue({
        bericht_email: contact.email
      });
    }
  }

  /**
   * Handle contact selection from dropdown by ID
   */
  onContactSelectedById(contactId: string): void {
    if (contactId) {
      const contact = this.availableContacts.find(c => c.id === contactId);
      if (contact) {
        this.onContactSelected(contact);
      }
    }
  }

  /**
   * Get contact display name for dropdown
   */
  getContactDisplayName(contact: Kontakt): string {
    if (!contact) return '';
    
    let displayName = contact.name || 'Unbekannter Kontakt';
    if (contact.email) {
      displayName += ` (${contact.email})`;
    }
    return displayName;
  }

  /**
   * Load report contacts from standort data
   */
  loadReportContacts(): void {
    console.log('🔍 Loading report contacts...');
    console.log('🔍 Standort bericht_email:', this.standort?.bericht_email);
    console.log('🔍 Type:', typeof this.standort?.bericht_email);
    
    if (this.standort?.bericht_email) {
      try {
        // Handle new array format
        if (Array.isArray(this.standort.bericht_email)) {
          this.reportContacts = this.standort.bericht_email;
          console.log('✅ Loaded array format contacts:', this.reportContacts);
        } else if (typeof this.standort.bericht_email === 'string') {
          // Check if it's JSON string
          if (this.standort.bericht_email.startsWith('[') || this.standort.bericht_email.startsWith('{')) {
            try {
              this.reportContacts = JSON.parse(this.standort.bericht_email);
              console.log('✅ Parsed JSON string contacts:', this.reportContacts);
            } catch (parseError) {
              console.error('Error parsing JSON string:', parseError);
              // Fallback to old string format
              this.reportContacts = [{
                email: this.standort.bericht_email,
                name: '',
                anrede: ''
              }];
            }
          } else {
            // Handle old string format (backward compatibility)
            this.reportContacts = [{
              email: this.standort.bericht_email,
              name: '',
              anrede: ''
            }];
            console.log('✅ Loaded old string format contact:', this.reportContacts);
          }
        }
      } catch (e) {
        console.error('Error parsing bericht_email:', e);
        this.reportContacts = [];
      }
    } else {
      this.reportContacts = [];
    }
    
    console.log('🔍 Final reportContacts:', this.reportContacts);
    
    // Update the form with the current contacts
    this.updateFormWithContacts();
  }

  /**
   * Add new contact to the list
   */
  addContact(): void {
    if (this.newContactEmail) {
      this.reportContacts.push({
        email: this.newContactEmail,
        name: this.newContactName || '',
        anrede: this.newContactAnrede || ''
      });
      
      // Clear the input fields
      this.newContactEmail = '';
      this.newContactName = '';
      this.newContactAnrede = '';
      
      // Update the form with JSON
      this.updateFormWithContacts();
    }
  }

  /**
   * Remove contact from the list
   */
  removeContact(index: number): void {
    this.reportContacts.splice(index, 1);
    this.updateFormWithContacts();
  }

  /**
   * Update form with current contacts as JSON
   */
  updateFormWithContacts(): void {
    this.standortForm.patchValue({
      bericht_email: JSON.stringify(this.reportContacts)
    });
  }

  /**
   * Handle contact selection from dropdown (for adding to list)
   */
  onContactSelectedForList(contact: Kontakt): void {
    if (contact && contact.email) {
      this.newContactEmail = contact.email;
      this.newContactName = contact.name;
      this.newContactAnrede = `Sehr geehrte/r ${contact.name}!`;
    }
  }

  /**
   * Handle contact selection from dropdown by ID (for adding to list)
   */
  onContactSelectedForListById(contactId: string): void {
    if (contactId) {
      const contact = this.availableContacts.find(c => c.id === contactId);
      if (contact) {
        this.onContactSelectedForList(contact);
      }
    }
  }

  /**
   * Optional email validator - only validates if email is provided
   */
  optionalEmailValidator(control: any) {
    if (!control.value || control.value.trim() === '') {
      return null; // No validation error for empty values
    }
    
    // Use the built-in email validator for non-empty values
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(control.value) ? null : { email: true };
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  // Objekt-Struktur methods
  loadObjektStruktur(): void {
    this.objektStrukturLoading = true;
    
    this.objektStrukturService.getFullObjektStruktur(this.standortId!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.objektStrukturData = response.data;
          this.objektStrukturLoading = false;
        },
        error: (error) => {
          console.error('Error loading objekt struktur:', error);
          this.objektStrukturLoading = false;
        }
      });
  }



  loadRoomIndicators(): void {
    // Load room indicators for this standort
    if (!this.standortId) {
      console.warn('No standort ID available for loading room indicators');
      this.roomIndicators = [];
      return;
    }

    this.objektStrukturService.getRoomIndikatorenByStandort(this.standortId, 200).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        if (Array.isArray(response)) {
          this.roomIndicators = response as any;
        } else if (response.data && Array.isArray(response.data)) {
          this.roomIndicators = response.data as any;
        } else {
          this.roomIndicators = [];
        }
        console.log('Room indicators loaded:', this.roomIndicators);
        this.applyStatusFilter();
      },
      error: (error) => {
        console.error('Error loading room indicators:', error);
        this.snackBar.open('Fehler beim Laden der Raum-Indikatoren', 'Schließen', { duration: 3000 });
        this.roomIndicators = [];
      }
    });
  }

  applyStatusFilter(): void {
    if (!this.roomIndicators) {
      this.filteredRoomIndicators = [];
      return;
    }
    if (this.selectedStatusFilter === -1) {
      this.filteredRoomIndicators = [...this.roomIndicators];
      return;
    }
    this.filteredRoomIndicators = this.roomIndicators.filter((ri: any) => {
      const statusVal = (ri as any)?.status;
      return Number(statusVal) === Number(this.selectedStatusFilter);
    });
  }

  onChangeStatusFilter(event: any): void {
    const val = event?.target?.value;
    this.selectedStatusFilter = typeof val === 'string' ? parseInt(val, 10) : val;
    if (Number.isNaN(this.selectedStatusFilter as any)) {
      this.selectedStatusFilter = 1;
    }
    this.applyStatusFilter();
  }

  toggleStatusMenu(): void {
    this.isStatusMenuOpen = !this.isStatusMenuOpen;
  }

  closeStatusMenu(): void {
    this.isStatusMenuOpen = false;
  }

  selectStatusFilter(value: number): void {
    this.selectedStatusFilter = value;
    this.applyStatusFilter();
    this.isStatusMenuOpen = false;
  }

  getStatusFilterLabel(): string {
    switch (Number(this.selectedStatusFilter)) {
      case 1: return 'Aktiv';
      case 0: return 'Inaktiv';
      default: return 'Alle';
    }
  }

  getStatusTextSimple(status: number | boolean | undefined): string {
    if (status === 1 || status === true) return 'Aktiv';
    if (status === 0 || status === false) return 'Inaktiv';
    return '-';
  }

  getStatusBadgeClasses(status: number | boolean | undefined): string {
    if (status === 1 || status === true) return 'bg-green-100 text-green-800 ring-green-600/20';
    return 'bg-red-100 text-red-800 ring-red-600/20';
  }

  getSymbolUrlFromIndicator(indicator: any): string | null {
    const symbolId = (indicator as any)?.indikator?.symbol_id;
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.png`;
  }

  getIndikatorCode(indicator: any): string {
    return (indicator as any)?.indikator?.code || '';
  }

  getIndicatorStatusClasses(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
    
    const isMoreBetter = indicator.mehr_weniger === 1;
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold 
        ? 'bg-green-50 text-green-700 ring-green-600/20' 
        : 'bg-red-50 text-red-700 ring-red-600/20';
    } else {
      return isBelowThreshold 
        ? 'bg-green-50 text-green-700 ring-green-600/20' 
        : 'bg-red-50 text-red-700 ring-red-600/20';
    }
  }

  getIndicatorIcon(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'help_outline';
    }
    
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold ? 'trending_up' : 'trending_down';
    } else {
      return isBelowThreshold ? 'trending_up' : 'trending_down';
    }
  }

  getIndicatorStatusText(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = parseFloat(indicator.schwellenwert);
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isNaN(value) || isNaN(threshold)) {
      return 'Unbekannt';
    }
    
    const isAboveThreshold = value > threshold;
    const isBelowThreshold = value < threshold;
    
    if (isMoreBetter) {
      return isAboveThreshold ? 'Gut' : 'Schlecht';
    } else {
      return isBelowThreshold ? 'Gut' : 'Schlecht';
    }
  }

  editRoomIndicator(indicator: RoomIndicator): void {
    this.snackBar.open(`Raum-Indikator "${indicator.name}" bearbeiten`, 'Schließen', { duration: 3000 });
  }

  expandAll() {
    this.expandNodesRecursive(this.objektStrukturData, true);
  }

  collapseAll() {
    this.expandNodesRecursive(this.objektStrukturData, false);
  }

  private expandNodesRecursive(nodes: ObjektStrukturNode[], expanded: boolean) {
    nodes.forEach(node => {
      node.expanded = expanded;
      if (node.children && node.children.length > 0) {
        this.expandNodesRecursive(node.children, expanded);
      }
    });
  }

  addNewNode() {
    console.log('Add new node functionality to be implemented');
    this.snackBar.open('Neues Element hinzufügen - Funktion wird implementiert', 'Schließen', { duration: 3000 });
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  onIndicatorAdded(): void {
    console.log('Indicator added, refreshing room indicators list');
    this.loadRoomIndicators();
  }


  printRoomIndicator(indicator: RoomIndicator): void {
    console.log('Opening label preview dialog for indicator:', indicator);
    
    const dialogRef = this.dialog.open(LabelPreviewDialogComponent, {
      width: '800px',
      height: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        indicator: indicator,
        standortId: this.standortId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Label preview dialog closed');
    });
  }

  printAllRoomIndicators(): void {
    console.log('Printing QR labels for all indicators');
    console.log('DEBUG: standortId:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }
    
    console.log('DEBUG: About to call generateQRIndicatorLabelsPDF');
    this.standortService.generateQRIndicatorLabelsPDF(this.standortId).subscribe({
      next: (pdfBlob: Blob) => {
        // Generate and download PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-labels-standort-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        const count = this.roomIndicators?.length || 0;
        this.snackBar.open(`${count} QR Labels PDF wird heruntergeladen`, 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
        this.snackBar.open('Fehler beim Generieren der PDF', 'Schließen', { duration: 5000 });
      }
    });
  }

  getAllLabelsDebugUrl(): string {
    if (!this.standortId) return '';
    const base = this.environment.apiUrl.replace('/v5', '');
    return `${base}/debug-pdf-qr-indicators/${this.standortId}?debug_html=1`;
  }

  private openPrintDialog(htmlContent: string, title: string): void {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              background: #f5f5f5;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 0; 
                background: white;
              }
              .no-print { display: none; }
              .label {
                page-break-after: always;
                margin-bottom: 0;
              }
              .label:last-child {
                page-break-after: avoid;
              }
            }
            .print-button {
              position: fixed;
              top: 10px;
              right: 10px;
              background: #007bff;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 4px;
              cursor: pointer;
              z-index: 1000;
              font-size: 14px;
            }
            .print-button:hover {
              background: #0056b3;
            }
            .print-info {
              position: fixed;
              top: 10px;
              left: 10px;
              background: #28a745;
              color: white;
              padding: 10px 15px;
              border-radius: 4px;
              z-index: 1000;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="print-info no-print">${title} - Bereit zum Drucken</div>
          <button class="print-button no-print" onclick="window.print()">🖨️ Drucken</button>
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      this.snackBar.open('Popup-Blocker verhindert das Öffnen des Druckfensters', 'Schließen', { duration: 5000 });
    }
  }

  
}

